import type { ColDef, ColGroupDef, GridOptions } from '@ag-grid-community/core';
import { DeviceStateAnalysisAsset } from '@cdw-ae/common-data';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { map, Observable } from 'rxjs';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { DevicesDataService } from '../../../../../common/services/device/devices-data.service';
import { OuPathService } from '../../../../../common/services/ou-path/ou-path.service';
import {
  filterToSupportedStateAnalysisFields,
  StateAnalysisReportFilterSettings,
} from '../../../../../common/services/route-config/configs/filter-bar-config.config';
import { createStorageByOuReportConfig } from '../configs/storage-by-ou-report.config';
import type {
  GaugeSummaryConfig,
  ReportData,
} from '../report-viewer/report-viewer.model';
import { BaseReportHandler } from './base-report.handler';

/**
 * Handler for Storage by OU aggregate reports
 * Manages filtering, grid configuration, and data processing for Storage reports
 * Structure: Organizational Unit Path (hierarchical tree)
 */
export class StorageByOuReportHandler extends BaseReportHandler {
  /**
   * Static method to load Storage by OU report data from API
   * Fetches device state analysis data, processes it with filters, and returns configured report data
   * @param devicesDataService Service to fetch device data
   * @param filters Optional filters to apply to the data
   * @returns Observable of ReportData ready for display
   */
  static load(
    devicesDataService: DevicesDataService,
    filters?: SelectedFilterField[]
  ): Observable<ReportData> {
    // Get filters to apply (use provided filters or initial filters)
    const allFilters =
      filters || StorageByOuReportHandler.getInitialFilters() || [];

    // Filter to only include fields supported by the state-analysis endpoint
    const filtersToApply = filterToSupportedStateAnalysisFields(allFilters);

    // Pass filters to API - filtering happens server-side now
    return devicesDataService.getDeviceStateAnalysis(filtersToApply).pipe(
      map((deviceStateAnalysisResponse) => {
        // Aggregate data (no filtering needed - API already filtered)
        const processedData = StorageByOuReportHandler.processRawData(
          deviceStateAnalysisResponse.data,
          [] // Empty filters - already filtered by API
        );

        // Create and return report config with processed data
        return createStorageByOuReportConfig(processedData);
      })
    );
  }

  private totalDevicesCount = 0;
  private storageSummary = {
    highStorage: 0,
    moderateStorage: 0,
    lowStorage: 0,
    criticalStorage: 0,
    unknown: 0,
  };
  private storageSummaryPercentages = {
    highStorage: 0,
    moderateStorage: 0,
    lowStorage: 0,
    criticalStorage: 0,
    unknown: 0,
  };

  // Gauge colors configuration
  private readonly gaugeColors = {
    green: [{ name: 'Green', value: TailwindColors.chartGreen }],
    yellow: [{ name: 'Yellow', value: TailwindColors.chartYellow }],
    orange: [{ name: 'Orange', value: TailwindColors.chartDarkOrange }],
    red: [{ name: 'Red', value: TailwindColors.chartRed }],
    gray: [{ name: 'Gray', value: TailwindColors.gray200 }],
  };

  /**
   * Calculate storage summary statistics from the data
   * Filters to leaf nodes and groups by storage status
   */
  private calculateStorageSummary(data: unknown[]): void {
    if (!data || data.length === 0) {
      return;
    }

    // Cast data to expected structure
    const storageData = data as Array<{
      ouPath: string;
      highStorage?: number;
      moderateStorage?: number;
      lowStorage?: number;
      criticalStorage?: number;
      unknown?: number;
    }>;

    // Reset summary
    this.storageSummary = {
      highStorage: 0,
      moderateStorage: 0,
      lowStorage: 0,
      criticalStorage: 0,
      unknown: 0,
    };
    this.totalDevicesCount = 0;

    // Sum all rows — each row represents devices directly in that OU (no parent-child overlap)
    storageData.forEach((item) => {
      this.storageSummary.highStorage += item.highStorage || 0;
      this.storageSummary.moderateStorage += item.moderateStorage || 0;
      this.storageSummary.lowStorage += item.lowStorage || 0;
      this.storageSummary.criticalStorage += item.criticalStorage || 0;
      this.storageSummary.unknown += item.unknown || 0;
    });

    // Calculate total devices (must include `unknown` so the header total reconciles
    // with the Battery Health by OU report, which also counts UNKNOWN-bucket devices)
    this.totalDevicesCount =
      this.storageSummary.highStorage +
      this.storageSummary.moderateStorage +
      this.storageSummary.lowStorage +
      this.storageSummary.criticalStorage +
      this.storageSummary.unknown;

    // Calculate percentages for gauges (0-100)
    if (this.totalDevicesCount > 0) {
      this.storageSummaryPercentages = {
        highStorage: Math.round(
          (this.storageSummary.highStorage / this.totalDevicesCount) * 100
        ),
        moderateStorage: Math.round(
          (this.storageSummary.moderateStorage / this.totalDevicesCount) * 100
        ),
        lowStorage: Math.round(
          (this.storageSummary.lowStorage / this.totalDevicesCount) * 100
        ),
        criticalStorage: Math.round(
          (this.storageSummary.criticalStorage / this.totalDevicesCount) * 100
        ),
        unknown: Math.round(
          (this.storageSummary.unknown / this.totalDevicesCount) * 100
        ),
      };
    } else {
      this.storageSummaryPercentages = {
        highStorage: 0,
        moderateStorage: 0,
        lowStorage: 0,
        criticalStorage: 0,
        unknown: 0,
      };
    }
  }

  /**
   * Initialize the handler with data to calculate storage summary
   */
  public override initializeWithData(data: unknown[]): void {
    this.calculateStorageSummary(data);
  }

  /**
   * Get gauge summary configuration for the Storage report
   * @param filteredData Optional filtered data to calculate summary from
   */
  public override getGaugeSummaryConfig(
    filteredData?: unknown[]
  ): GaugeSummaryConfig {
    // If filtered data is provided, recalculate the summary
    if (filteredData && filteredData.length > 0) {
      this.calculateStorageSummary(filteredData);
    }

    return {
      headerText: `${this.totalDevicesCount} Devices`,
      cards: [
        {
          title: 'High Storage (>50% free)',
          value: this.storageSummary.highStorage,
          percentage: this.storageSummaryPercentages.highStorage,
          borderColor: TailwindColors.chartGreen,
          gaugeColor: this.gaugeColors.green,
          iconImage: 'images/reports/storage_by_ou/laptop_green.svg',
          showValue: false,
        },
        {
          title: 'Moderate Storage (20-50% free)',
          value: this.storageSummary.moderateStorage,
          percentage: this.storageSummaryPercentages.moderateStorage,
          borderColor: TailwindColors.chartYellow,
          gaugeColor: this.gaugeColors.yellow,
          iconImage: 'images/reports/storage_by_ou/laptop_yellow.svg',
          showValue: false,
        },
        {
          title: 'Low Storage (10-20% free)',
          value: this.storageSummary.lowStorage,
          percentage: this.storageSummaryPercentages.lowStorage,
          borderColor: TailwindColors.chartDarkOrange,
          gaugeColor: this.gaugeColors.orange,
          iconImage: 'images/reports/storage_by_ou/laptop_amber.svg',
          showValue: false,
        },
        {
          title: 'Critical Storage (<10% free)',
          value: this.storageSummary.criticalStorage,
          percentage: this.storageSummaryPercentages.criticalStorage,
          borderColor: TailwindColors.chartRed,
          gaugeColor: this.gaugeColors.red,
          iconImage: 'images/reports/storage_by_ou/laptop_red.svg',
          showValue: false,
        },
        {
          title: 'Unknown',
          value: this.storageSummary.unknown,
          percentage: this.storageSummaryPercentages.unknown,
          borderColor: TailwindColors.gray200,
          gaugeColor: this.gaugeColors.gray,
          iconImage: 'images/reports/storage_by_ou/laptop_gray.svg',
          showValue: false,
        },
      ],
    };
  }

  getColumnDefs(): (ColDef | ColGroupDef)[] {
    const config = this.reportData.aggregateConfig;
    if (!config) return [];

    // Add aggregation to the columns while preserving all existing properties
    return config.columns.map((col) => {
      if ('field' in col && col.field) {
        // Add aggregation for numeric columns
        const numericFields = [
          'highStorage',
          'moderateStorage',
          'lowStorage',
          'criticalStorage',
          'unknown',
        ];
        if (numericFields.includes(col.field)) {
          return {
            ...col,
            aggFunc: 'sum', // Aggregate child values by summing
          };
        }
      }
      return col;
    });
  }

  getGridOptions(): GridOptions {
    const config = this.reportData.aggregateConfig;
    if (!config) return {};

    return {
      treeData: true,
      animateRows: true,
      groupDefaultExpanded: 0, // Start collapsed
      suppressColumnVirtualisation: true, // Force all columns to render
      defaultColDef: {
        resizable: true,
      },
      autoGroupColumnDef: {
        headerName: 'OU Path',
        minWidth: 400,
        cellRendererParams: {
          suppressCount: true,
        },
      },
      getDataPath: (data) => {
        // Convert ouPath segments - first segment no slash, subsequent ones with slash
        // Example: "/Engineering/Development" -> ["Engineering", "/Development"]
        // Note: Status is stored in each row but not part of the tree hierarchy
        // When multiple rows share the same path (e.g., same OU but different status),
        // AG Grid will aggregate them using aggFunc='sum'
        const path = (data as { ouPath?: string }).ouPath || '';
        return OuPathService.parsePathForGrid(path);
      },
    };
  }

  static override getFilterBarSettings(): FilterBarField[] {
    return StateAnalysisReportFilterSettings;
  }

  static override getInitialFilters(): SelectedFilterField[] | null {
    return BaseReportHandler.getDefaultActiveStatusFilter(
      StateAnalysisReportFilterSettings
    );
  }

  /**
   * Process raw DeviceStateAnalysisAsset data into storage by OU format
   * Aggregates data by organizational unit
   */
  static override processRawData(
    rawData: unknown[],
    _filters: SelectedFilterField[]
  ): unknown[] {
    const deviceData = rawData as DeviceStateAnalysisAsset[];

    // Aggregate storage data by OU
    const ouStorageLookupByOu = deviceData.reduce(
      (accumulator, currentValue) => {
        const ouPath = currentValue.groupingValue || 'Unknown OU';

        if (!accumulator[ouPath]) {
          accumulator[ouPath] = {
            ouPath,
            status: currentValue.status,
            lowStorage: 0,
            moderateStorage: 0,
            highStorage: 0,
            criticalStorage: 0,
            unknown: 0,
          } as Record<string, string | number>;
        }

        if (String(currentValue.diskUsed) === 'LOW') {
          accumulator[ouPath].lowStorage =
            Number(accumulator[ouPath].lowStorage) + currentValue.deviceCount;
        } else if (
          String(currentValue.diskUsed) === '50' ||
          String(currentValue.diskUsed) === '60'
        ) {
          accumulator[ouPath].moderateStorage =
            Number(accumulator[ouPath].moderateStorage) +
            currentValue.deviceCount;
        } else if (
          String(currentValue.diskUsed) === '70' ||
          String(currentValue.diskUsed) === '80'
        ) {
          accumulator[ouPath].highStorage =
            Number(accumulator[ouPath].highStorage) + currentValue.deviceCount;
        } else if (String(currentValue.diskUsed) === '90') {
          accumulator[ouPath].criticalStorage =
            Number(accumulator[ouPath].criticalStorage) +
            currentValue.deviceCount;
        } else {
          accumulator[ouPath].unknown =
            Number(accumulator[ouPath].unknown) + currentValue.deviceCount;
        }

        return accumulator;
      },
      {} as Record<string, Record<string, string | number>>
    );

    const rows = Object.values(ouStorageLookupByOu) as Array<
      { ouPath: string } & Record<string, unknown>
    >;
    return OuPathService.redistributeNonLeafData(rows, [
      'highStorage',
      'moderateStorage',
      'lowStorage',
      'criticalStorage',
      'unknown',
    ]);
  }
}
